import { useEffect, useRef, useState } from 'react'

import { getSignUrl, uploadFile } from 'api/upload'
import { FileView, TextField, UploadBox } from 'components'
import { MessageAssetQueryError } from 'components/MessageAssetQueryError'
import {
  ACCEPT_LINE_FLEX_IMAGES,
  MAX_LINE_IMAGE_SIZE,
  ONE_MB,
  sendAllMessageStrings,
  sendErrorMessages,
  surveySelectorStrings,
} from '../../../constants'
import useToast from 'hooks/useToast'
import { useSurveys } from 'hooks/useSurveys'
import type { TSurveyImageAspectRatio, TSurveySection } from 'types'
import { cn } from 'utils/cn'
import {
  formatAcceptedTypes,
  hasJpegOrPngSignature,
  validateFile,
} from 'utils/file'
import generateFileName from 'utils/generateFileName'

// LINEのTEXTメッセージ上限。超過するとLINEが全受信者向けに拒否する（backendでも検証）
const SURVEY_GUIDE_MAX_LENGTH = 5000

type Props = {
  section: TSurveySection
  onUpdateSection: (patch: Partial<TSurveySection>) => void
  /**
   * 招待画像は選択直後にアップロードするため、完了前に親フォームが
   * 送信・保存するとimageUrl未設定のmessageが確定する。親はこの通知で
   * アップロード中の送信・保存を無効化する。
   */
  onUploadingChange?: (uploading: boolean) => void
}

export function SurveySectionSelector({
  section,
  onUpdateSection,
  onUploadingChange,
}: Props) {
  const { toastError } = useToast()
  const { surveys, loading, error, refetch } = useSurveys()
  const [uploading, setUploading] = useState(false)
  const onUploadingChangeRef = useRef(onUploadingChange)
  onUploadingChangeRef.current = onUploadingChange

  useEffect(() => {
    // アップロード中にsection削除・タイプ変更でunmountされた場合、
    // 親の無効化が残留しないよう解除を通知する
    return () => {
      onUploadingChangeRef.current?.(false)
    }
  }, [])
  const selectableSurveys = surveys.filter(
    (survey) =>
      survey.status === 'ACTIVE' && survey.responseFormat === 'FORM',
  )
  const selectedSurveyIsInvalid =
    section.surveyId !== undefined &&
    !selectableSurveys.some((survey) => survey.id === section.surveyId)

  useEffect(() => {
    if (!loading && !error && selectedSurveyIsInvalid) {
      onUpdateSection({ surveyId: undefined })
    }
  }, [error, loading, onUpdateSection, selectedSurveyIsInvalid])

  const handleSelectInviteImage = async (file: File) => {
    if (uploading) return
    const isValid = validateFile({
      file,
      acceptedTypes: ACCEPT_LINE_FLEX_IMAGES,
      maxBytes: MAX_LINE_IMAGE_SIZE,
      toastError,
    })
    if (!isValid) return
    if (!(await hasJpegOrPngSignature(file))) {
      toastError(
        new Error(
          sendErrorMessages.invalidFileFormat.replace(
            '$formats',
            formatAcceptedTypes(ACCEPT_LINE_FLEX_IMAGES),
          ),
        ),
      )
      return
    }

    setUploading(true)
    onUploadingChange?.(true)
    try {
      const signed = await getSignUrl({
        filename: generateFileName(file),
        contentType: file.type,
        size: file.size,
      })
      await uploadFile(signed.preSignedUrl, file)
      onUpdateSection({
        imageUrl: signed.uploadedUrl,
        imageAspectRatio: section.imageAspectRatio ?? '16:9',
      })
    } catch (uploadError: unknown) {
      toastError(
        uploadError instanceof Error
          ? uploadError
          : new Error('画像のアップロードに失敗しました。'),
      )
    } finally {
      setUploading(false)
      onUploadingChangeRef.current?.(false)
    }
  }

  const setAspectRatio = (imageAspectRatio: TSurveyImageAspectRatio) => {
    onUpdateSection({ imageAspectRatio })
  }

  return (
    <div className="space-y-4 p-4 bg-[#F9FAFB]">
      {error ? (
        <MessageAssetQueryError
          message={surveySelectorStrings.loadFailed}
          retryLabel={surveySelectorStrings.retry}
          retryingLabel={surveySelectorStrings.retrying}
          isRetrying={loading}
          onRetry={() => {
            void refetch()
          }}
        />
      ) : null}
      <div>
        <select
          aria-label={surveySelectorStrings.label}
          className={cn(
            'w-full rounded-xl border border-border bg-white p-3 text-sm outline-none focus:border-accent',
            !section.surveyId && 'text-muted',
          )}
          value={section.surveyId ?? ''}
          disabled={loading || Boolean(error)}
          onChange={(event) =>
            onUpdateSection({
              surveyId: event.target.value
                ? Number(event.target.value)
                : undefined,
            })
          }
        >
          <option value="">{surveySelectorStrings.placeholder}</option>
          {selectableSurveys.map((survey) => (
            <option key={survey.id} value={survey.id}>
              {survey.name}
            </option>
          ))}
        </select>
        {!section.surveyId ? (
          <p className="mt-2 text-xs text-muted">
            {surveySelectorStrings.emptyHint}
          </p>
        ) : null}
      </div>

      <TextField
        type="textarea"
        name={`survey-guide-${section.id}`}
        label={surveySelectorStrings.guideLabel}
        value={section.content ?? ''}
        onChange={(event) => onUpdateSection({ content: event.target.value })}
        placeholder={surveySelectorStrings.guidePlaceholder}
        className="w-full bg-white"
        rows={3}
        maxLength={SURVEY_GUIDE_MAX_LENGTH}
      />

      <div className="grid gap-2">
        <span className="text-sm text-neutral-600">
          {surveySelectorStrings.inviteImageLabel}
        </span>
        {section.imageUrl ? (
          <div className="grid gap-3">
            <div className="flex h-36 overflow-hidden rounded-xl bg-white p-2">
              <FileView
                className="h-full max-h-32 rounded-lg object-cover"
                file={{ url: section.imageUrl, type: 'image/png' }}
                onRemove={() =>
                  onUpdateSection({
                    imageUrl: undefined,
                    imageAspectRatio: undefined,
                  })
                }
              />
            </div>
            <div className="grid gap-1.5">
              <span className="text-xs font-semibold text-ink">
                {surveySelectorStrings.inviteImageAspectLabel}
              </span>
              <div
                role="radiogroup"
                aria-label={surveySelectorStrings.inviteImageAspectLabel}
                className="inline-flex overflow-hidden rounded-xl border border-border/80"
              >
                {(['1:1', '16:9'] as const).map((ratio, index) => {
                  const checked = (section.imageAspectRatio ?? '16:9') === ratio
                  return (
                    <button
                      key={ratio}
                      type="button"
                      className={cn(
                        'px-3 py-1.5 text-xs font-semibold transition-[background-color,color]',
                        checked
                          ? 'bg-accent-soft text-ink'
                          : 'bg-white text-muted hover:bg-white/80',
                        index > 0 && 'border-l border-border/80',
                      )}
                      aria-pressed={checked}
                      onClick={() => setAspectRatio(ratio)}
                    >
                      {ratio}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          <UploadBox
            title={
              uploading
                ? 'アップロード中...'
                : sendAllMessageStrings.uploadPhoto
            }
            icon="image"
            acceptTypes={ACCEPT_LINE_FLEX_IMAGES}
            maxSizeMb={MAX_LINE_IMAGE_SIZE / ONE_MB}
            onSelect={(file) => {
              void handleSelectInviteImage(file)
            }}
          />
        )}
      </div>
    </div>
  )
}
